import { usePanelActionsInternal } from './panel-provider';
import type { PanelSystemState } from './types';
import { usePanelStateInternal } from './use-panel-state-internal';

/**
 * React hook to access the panel manager and state.
 * Must be called within a PanelProvider.
 *
 * **Important:** This hook reads from two separate contexts:
 * - Manager context is stable and never changes
 * - State context updates when panels open/close/resize
 *
 * If you only need the manager (to call openPanel, closePanel, etc.),
 * consider using usePanelActions() instead to avoid unnecessary re-renders.
 *
 * @throws {Error} If called outside of a PanelProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { state, manager } = usePanelManager();
 *   return (
 *     <>
 *       <button onClick={() => manager.openPanel('id-1', entryPoint)}>
 *         Open
 *       </button>
 *       {state.activePanels.map(panel => (
 *         <Panel key={panel.instanceId} panel={panel} />
 *       ))}
 *     </>
 *   );
 * }
 * ```
 */
export function usePanelManager(): {
	state: PanelSystemState;
	manager: ReturnType<typeof usePanelActionsInternal>;
} {
	const manager = usePanelActionsInternal();
	const state = usePanelStateInternal();

	return {
		state,
		manager,
	};
}
